import { Component, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { captureError } from "@/utils/sentry";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  info?: React.ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep logging for devtools, but also store for on-screen diagnostics.
    console.error("Error boundary caught:", error, info);
    captureError(error, { componentStack: info.componentStack });
    this.setState({ error, info });
  }

  render() {
    if (this.state.hasError) {
      const message = this.state.error?.message || "Unknown error";
      const stack = this.state.error?.stack;
      const componentStack = this.state.info?.componentStack;
      const showDetails = import.meta.env.DEV;

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="text-center space-y-6 max-w-md w-full">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 mx-auto">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <div className="space-y-2">
              <h1 className="text-h2 text-foreground">Something went wrong</h1>
              <p className="text-muted-foreground">{message}</p>
            </div>

            {showDetails && (stack || componentStack) && (
              <details className="text-left rounded-lg border border-border bg-card p-4">
                <summary className="cursor-pointer text-sm text-foreground">
                  Show error details
                </summary>
                <div className="mt-3 space-y-3">
                  {stack && (
                    <pre className="text-xs overflow-auto whitespace-pre-wrap text-muted-foreground">
                      {stack}
                    </pre>
                  )}
                  {componentStack && (
                    <pre className="text-xs overflow-auto whitespace-pre-wrap text-muted-foreground">
                      {componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            <Button onClick={() => window.location.reload()} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
